import { RefreshCw } from "lucide-react";
import { EquipmentPanel } from "@/components/character/equipment-panel";
import { FeaturesPanel } from "@/components/character/features-panel";
import { InventoryPanel } from "@/components/character/inventory-panel";
import { ProgressionPanel } from "@/components/character/progression-panel";
import { SheetPanel } from "@/components/character/sheet-panel";
import { SpellbookPanel } from "@/components/character/spellbook-panel";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCharacterSheet } from "@/hooks/use-character-sheet";
import type { PartyPc } from "@/lib/core/schemas/party-view";
import { cn } from "@/lib/utils";

export function CharacterTab({
  pcs,
  selectedId,
  onSelect,
  selectedPc,
  onRefresh,
  campaignId,
}: {
  pcs: PartyPc[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  selectedPc: PartyPc | null;
  onRefresh: () => void;
  campaignId: string;
}) {
  const { sheet } = useCharacterSheet(campaignId, selectedPc?.id ?? null);
  if (pcs.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-4 text-center text-muted-foreground">
        <p className="text-sm">No player characters in this campaign.</p>
        <Button onClick={onRefresh} size="sm" variant="outline">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full pb-16 md:pb-0">
      <div className="mx-auto max-w-2xl space-y-4 p-4">
        {/* Header: PC selector + refresh */}
        <div className="flex items-center justify-between gap-2">
          {pcs.length > 1 ? (
            <div className="flex flex-wrap gap-2">
              {pcs.map((pc) => (
                <button
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    selectedId === pc.id
                      ? "bg-primary/20 text-primary"
                      : "bg-secondary text-foreground hover:bg-secondary/80"
                  )}
                  key={pc.id}
                  onClick={() => onSelect(pc.id)}
                  type="button"
                >
                  {pc.name}
                </button>
              ))}
            </div>
          ) : (
            <h2 className="font-semibold text-foreground">
              {selectedPc?.name ?? "Character"}
            </h2>
          )}
          <Button
            aria-label="Refresh party data"
            onClick={onRefresh}
            size="icon"
            variant="ghost"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Inner tabs */}
        <Tabs className="w-full" defaultValue="equipment">
          <TabsList className="w-full justify-start overflow-x-auto bg-secondary">
            <TabsTrigger className="shrink-0" value="sheet">
              Sheet
            </TabsTrigger>
            {sheet?.spellPreparation && (
              <TabsTrigger className="shrink-0" value="spells">
                Spells
              </TabsTrigger>
            )}
            <TabsTrigger className="shrink-0" value="equipment">
              Equipment
            </TabsTrigger>
            <TabsTrigger className="shrink-0" value="inventory">
              Inventory
            </TabsTrigger>
            <TabsTrigger className="shrink-0" value="features">
              Features
            </TabsTrigger>
            <TabsTrigger className="shrink-0" value="progression">
              Progression
            </TabsTrigger>
          </TabsList>
          <TabsContent value="sheet">
            <SheetPanel campaignId={campaignId} pc={selectedPc} />
          </TabsContent>
          {sheet?.spellPreparation && (
            <TabsContent value="spells">
              <SpellbookPanel
                campaignId={campaignId}
                onChange={onRefresh}
                pc={selectedPc}
              />
            </TabsContent>
          )}
          <TabsContent value="equipment">
            <EquipmentPanel
              campaignId={campaignId}
              onChange={onRefresh}
              pc={selectedPc}
            />
          </TabsContent>
          <TabsContent value="inventory">
            <InventoryPanel pc={selectedPc} />
          </TabsContent>
          <TabsContent value="features">
            <FeaturesPanel campaignId={campaignId} pc={selectedPc} />
          </TabsContent>
          <TabsContent value="progression">
            <ProgressionPanel
              campaignId={campaignId}
              onChange={onRefresh}
              pc={selectedPc}
            />
          </TabsContent>
        </Tabs>
      </div>
    </ScrollArea>
  );
}
